// Refine: a bottomless sink for star items. Each step adds +1 to one base
// stat of one creature (HP, Attack, Defense, Magic or Resist; never Speed),
// counted before talent and every multiplier, so it is worth most on great
// creatures. It never changes the rating or grade: those measure the rolls.
//
// Cost per stat is cubic: reaching +N costs REFINE_BASE × N³ items in total,
// so step N costs REFINE_BASE × (3N² − 3N + 1). There is no cap.
//
// Items: the star item of the creature's region (Rune of Ascent for Midgard
// creatures, Olympian Ember for Hellas, Dragon Pearl for Yomi), the material
// that drops on that region's last hunts, or the star item of any later
// region, as with stars, where later items carry further. So an Ember can
// refine a Midgard creature, but a Rune never refines a Hellas one. One step
// is paid in one kind of item; the count is the same whichever kind.
//
// Why 70: the reference game uses 500 with about 250 items of one kind a day
// for a top player, so +1 takes two days, +3 two months and +5 eight months.
// Star items drop 1 in 250 kills, only on a region's last hunts, so our top
// player (the grinder bot, 18 h a day) gets about 35 a day over its first two
// weeks (2.0 an hour measured over 336 h; up to about 110 on a day spent on a
// star hunt). 500 × 35 / 250 = 70 keeps the reference pace: +1 = 70 items
// (two days), +3 = 1,890 (54 days), +5 = 8,750 (250 days, about eight months).
// sim/targets.ts checks the weekly pace.
//
// 70 -> 40 (daily tuning, 2026-10-15): stars now take most star items first,
// so in a week no bot held the 70 for a first step (refine sink 0 since
// 2026-10-14). At 40 bots buy their first steps in week one; +3 = 1,080 and
// +5 = 5,000 items.
import { STAR_ITEM } from './items.ts';
import { SPECIES, type RegionId } from './species.ts';

const REGION_ORDER: RegionId[] = ['midgard', 'hellas', 'yomi'];

export const REFINE_BASE = 40;
/** Stat indices that can be refined (STAT_NAMES order): HP, Attack, Defense, Magic, Resist. Speed (5) cannot. */
export const REFINE_STATS = [0, 1, 2, 3, 4] as const;
/** Most steps one message may buy at once. */
export const MAX_REFINE_STEPS = 10;

/** Items for step n (1-based) of one stat. */
export function refineStepCost(n: number): number {
  return REFINE_BASE * (3 * n * n - 3 * n + 1);
}

/** Items for +n in one stat, from +0. */
export function refineTotalCost(n: number): number {
  return REFINE_BASE * n * n * n;
}

/** Items to go from +from to +to in one stat. */
export function refineCostBetween(from: number, to: number): number {
  return refineTotalCost(to) - refineTotalCost(from);
}

/** The creature's own refine item: its region's star item. */
export function refineItem(speciesId: string): string {
  return STAR_ITEM[SPECIES[speciesId].region];
}

/** Every item that can refine this species: its region's star item first, then later regions'. */
export function refineItems(speciesId: string): string[] {
  return REGION_ORDER.slice(REGION_ORDER.indexOf(SPECIES[speciesId].region)).map((r) => STAR_ITEM[r]);
}

export function canRefineWith(speciesId: string, item: string): boolean {
  return refineItems(speciesId).includes(item);
}

/** A creature's refine steps per stat (always 5 entries). */
export function refineOf(c: { refine?: readonly number[] }): number[] {
  return REFINE_STATS.map((i) => Math.max(0, Math.floor(c.refine?.[i] ?? 0)));
}

/** Total refine steps over all stats. */
export function refineTotal(c: { refine?: readonly number[] }): number {
  return refineOf(c).reduce((a, b) => a + b, 0);
}

/** Items already spent refining this creature. */
export function refineSpent(c: { refine?: readonly number[] }): number {
  return refineOf(c).reduce((a, n) => a + refineTotalCost(n), 0);
}

export function canRefineStat(i: number): boolean {
  return (REFINE_STATS as readonly number[]).includes(i);
}
